/**
 * Athlete App - Videos Page
 * Upload and view training videos with coach feedback
 */

import { useState, useEffect, useRef } from 'react';
import {
  listVideos,
  requestUploadUrl,
  uploadToS3,
  completeUpload,
  listComments,
  generateVideoTitle,
  type Organization,
  type Video,
} from '@tss/shared';
import { Video as VideoIcon, Upload, Clock, CheckCircle, Plus, X, Loader2 } from 'lucide-react';
import { VideoPlayerModal } from '../components/VideoPlayerModal';
import { Button, Input, Textarea } from '@tss/shared/ui';
import { PageHeader, TileGrid, VideoTile, EmptyState, Skeleton, StatCard } from '../components/UI';

interface VideosPageProps {
  org: Organization | null;
  isGuardian?: boolean;
  proxyAthleteId?: string;
}

// Track feedback counts per video
interface VideoWithFeedback extends Video {
  feedbackCount?: number;
}

export function VideosPage({ org, isGuardian = false, proxyAthleteId }: VideosPageProps) {
  const [videos, setVideos] = useState<VideoWithFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  useEffect(() => {
    loadVideos();
  }, [org?.org_id, proxyAthleteId]);

  async function loadVideos() {
    setLoading(true);
    try {
      // Pass org_id and proxyAthleteId for filtering in proxy mode
      const { videos: videoList } = await listVideos(org?.org_id, false, proxyAthleteId);

      // Fetch feedback counts for each video in parallel
      const videosWithFeedback = await Promise.all(
        videoList.map(async (video) => {
          try {
            const { comments } = await listComments(video.video_id);
            return { ...video, feedbackCount: comments?.length || 0 };
          } catch {
            return { ...video, feedbackCount: 0 };
          }
        })
      );

      setVideos(videosWithFeedback);
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleUploadComplete() {
    setShowUploadModal(false);
    loadVideos();
  }

  function handleVideoUpdate() {
    loadVideos();
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <PageHeader
        title={isGuardian ? 'Athlete Videos' : 'My Videos'}
        description={
          isGuardian
            ? "View your athlete's training videos and coach feedback"
            : 'Upload videos for coach review and feedback'
        }
        icon={VideoIcon}
        iconColor="var(--color-success)"
        actions={
          (!isGuardian || proxyAthleteId) && (
            <Button onClick={() => setShowUploadModal(true)} iconLeft={<Plus size={18} />}>
              Upload Video
            </Button>
          )
        }
      />

      {/* Stats */}
      <div
        className="grid gap-4 mb-6"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' }}
      >
        <StatCard
          value={videos.length.toString()}
          label="Total Videos"
          color="var(--purple-600)"
          icon={VideoIcon}
        />
        <StatCard
          value={videos.filter((v) => v.status === 'READY').length.toString()}
          label="Reviewed"
          color="var(--color-success)"
          icon={CheckCircle}
        />
        <StatCard
          value={videos.filter((v) => v.status === 'PROCESSING').length.toString()}
          label="Processing"
          color="#f59e0b"
          icon={Clock}
        />
      </div>

      {loading ? (
        <Skeleton.VideoCard count={4} />
      ) : videos.length === 0 ? (
        <EmptyState.Videos
          onAction={!isGuardian || proxyAthleteId ? () => setShowUploadModal(true) : undefined}
        />
      ) : (
        <TileGrid
          items={videos}
          keyExtractor={(video) => video.video_id}
          renderTile={(video) => (
            <VideoTile video={video} onClick={() => setSelectedVideo(video)} />
          )}
          showViewToggle={false}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          orgId={org?.org_id}
          proxyAthleteId={proxyAthleteId}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayerModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
          onUpdate={handleVideoUpdate}
        />
      )}
    </div>
  );
}

interface UploadModalProps {
  orgId?: string;
  proxyAthleteId?: string; // For guardian proxy mode
  onClose: () => void;
  onComplete: () => void;
}

function UploadModal({ orgId, proxyAthleteId, onClose, onComplete }: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
      // Validate file size (500MB max)
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
      // Auto-fill title from filename with smart cleanup
      if (!title) {
        setTitle(generateVideoTitle(selectedFile.name));
      }
    }
  }

  async function handleUpload() {
    if (!file || !title) return;

    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Step 1: Request upload URL (includes title/description for video metadata)
      const { upload_url, video_id, fields } = await requestUploadUrl({
        org_id: orgId!,
        filename: file.name,
        file_size: file.size,
        title,
        description: description || undefined,
        athlete_id: proxyAthleteId, // For guardian proxy mode
      });

      // Step 2: Upload to S3 with progress using presigned POST fields
      await uploadToS3(upload_url, fields, file, (percent) => {
        setProgress(percent);
      });

      // Step 3: Complete upload (just needs video_id)
      await completeUpload(video_id);

      onComplete();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setUploading(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 'min(500px, calc(100vw - 32px))',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-between" style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
            Upload Video
          </h2>
          <Button variant="ghost" onClick={onClose} iconLeft={<X size={20} />} />
        </div>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* File drop zone */}
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '2px dashed var(--gray-300)',
            borderRadius: 'var(--radius-md)',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            marginBottom: '20px',
            background: file ? 'rgba(var(--purple-rgb), 0.05)' : 'transparent',
            borderColor: file ? 'var(--purple-600)' : 'var(--gray-300)',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file ? (
            <div>
              <VideoIcon size={32} style={{ color: 'var(--purple-600)', marginBottom: '8px' }} />
              <p style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{file.name}</p>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            </div>
          ) : (
            <div>
              <Upload size={32} style={{ color: 'var(--gray-400)', marginBottom: '8px' }} />
              <p style={{ fontWeight: '500', color: 'var(--gray-700)' }}>Click to select a video</p>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
                MP4, MOV, or WebM up to 500MB
              </p>
            </div>
          )}
        </div>

        {/* Title & Description */}
        <div className="form-group">
          <label>Title</label>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Practice Session - Free Throws"
            disabled={uploading}
          />
        </div>

        <div className="form-group">
          <label>Description (optional)</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add notes about this video..."
            rows={3}
            disabled={uploading}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginBottom: '20px' }}>
            <div className="flex-between" style={{ marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>Uploading...</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--purple-600)' }}>
                {progress}%
              </span>
            </div>
            <div
              style={{
                height: '8px',
                background: 'var(--gray-200)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  background:
                    'linear-gradient(90deg, var(--purple-600) 0%, var(--purple-400) 100%)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={uploading} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || !title || uploading}
            loading={uploading}
            iconLeft={
              uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />
            }
            style={{ flex: 1 }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
}
